import uniqBy from 'lodash/uniqBy';
import React, { useContext } from 'react';
import { IPromotion } from 'common/promotion';
import { IItinerary } from 'common/types';
import { InternationalContext } from 'contexts/international';
import { TripType } from 'contexts/searchParams';
import { getPriceOfPerson } from 'list/international/utils';
import { IInternationalContextResult, SelectTypeKeys } from '../types';
import IndividualList from './list';
import { isValidArrivalSchedule } from '../utils';

interface IProps {
  fareType: string;
  flightResult: IInternationalContextResult;
  itinerary: IItinerary[];
  promotions: IPromotion[];
  scheduleTitles: string[];
  selectedFlightList: [string, string, string][];
  tripType: TripType;
  selectFlight: (
    index: number,
    sch: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const IndivisualListContainer = (props: IProps) => {
  const {
    fareType,
    flightResult,
    promotions,
    scheduleTitles,
    selectedFlightList,
    tripType,
    selectFlight,
    setMinFare,
  } = props;
  const { sortSchs } = useContext(InternationalContext);
  const { schs, itinerary, fares } = flightResult;

  // 현재 여정까지 선택된 스캐쥴ID조합
  const selectedSchs = selectedFlightList.map(([sch]) => sch).join('|');
  if (selectedFlightList.length >= itinerary.length) return null;

  const nextIndex = selectedFlightList.length;

  const getTargetSchedule = (id: string) =>
    id
      .split('|')
      .slice(0, nextIndex + 1)
      .join('|');

  // 마지막 여정에서 선택된 요금조건
  const curFareType: string =
    selectedFlightList.length > 0
      ? selectedFlightList[selectedFlightList.length - 1][1]
      : '';
  // 1여정에서는 전체, 그 이후에는 다른 요금 조건 그룹
  let finalList: string[] = [];
  // 동일 요금 조건 그룹
  let sameFareTypes: string[] = [];

  const schMapByLowestFare = new Map<string, [number, string]>();

  function updateLowestFareBySchedule(sch: string) {
    const fare = fares.get(sch);
    if (!fare) return;
    const lowestFare = Object.values(fare)
      .map(fare => getPriceOfPerson(fare[0].Adult, true))
      .sort((a, b) => a - b)[0];
    const targetSch = getTargetSchedule(sch);
    const [prevLowestFare] = schMapByLowestFare.get(targetSch) || [
      Number.MAX_SAFE_INTEGER,
      sch,
    ];
    if (lowestFare < prevLowestFare) {
      schMapByLowestFare.set(targetSch, [lowestFare, sch]);
    }
  }

  if (curFareType) {
    const matchedSchedules = Array.from(fares.keys()).filter(sch =>
      sch.startsWith(selectedSchs),
    );
    // 현재까지 선택된 스캐쥴들과 요금조건으로 최저 ota요금들을 취합
    const adultFares = matchedSchedules
      .map(sch => {
        const fareData = fares.get(sch)?.[curFareType] ?? null;
        return fareData ? getPriceOfPerson(fareData[0].Adult, true) : null;
      })
      .filter(fare => fare && fare > 0);
    // 취합된 최저 ota요금들 중에서 최저가 추출
    const lowestFare = Math.min(...(adultFares as number[]));
    // 추출된 최저가를 기준으로 최저가 요금 그룹과 다른 요금 그룹으로 분류
    matchedSchedules.forEach(sch => {
      const fareData = fares.get(sch)?.[curFareType] ?? null;
      if (fareData) {
        const adultFare = getPriceOfPerson(fareData[0].Adult, true);
        if (adultFare === lowestFare) {
          sameFareTypes.push(sch);
        } else {
          updateLowestFareBySchedule(sch);
        }
      } else {
        updateLowestFareBySchedule(sch);
      }
    });
    // 각 그룹별 정렬
    sameFareTypes = sortSchs(
      uniqBy(sameFareTypes, getTargetSchedule),
      SelectTypeKeys.indivisual,
      flightResult,
    )
      .flat()
      .filter(filteredSchedules => {
        if (selectedFlightList.length < 1) {
          return true;
        }
        const departureScheduleKey =
          selectedFlightList?.[selectedFlightList.length - 1]?.[0];
        const arrivalScheduleKey =
          filteredSchedules.split('|')[selectedFlightList.length];

        // TODO: 추후 서버에서 validation 되도록 국제선 이관 시 적용
        return isValidArrivalSchedule(
          flightResult.schedules || [],
          departureScheduleKey,
          arrivalScheduleKey,
          selectedFlightList.length,
        );
      });
    const filteredSchs = Array.from(schMapByLowestFare.values()).map(
      ([_, fschId]) => fschId,
    );
    finalList = sortSchs(filteredSchs, SelectTypeKeys.indivisual, flightResult)
      .flat()
      .filter(filteredSchedules => {
        if (selectedFlightList.length < 1) {
          return true;
        }
        const departureScheduleKey =
          selectedFlightList?.[selectedFlightList.length - 1]?.[0];
        const arrivalScheduleKey =
          filteredSchedules.split('|')[selectedFlightList.length];

        // TODO: 추후 서버에서 validation 되도록 국제선 이관 시 적용
        return isValidArrivalSchedule(
          flightResult.schedules || [],
          departureScheduleKey,
          arrivalScheduleKey,
          selectedFlightList.length,
        );
      });
  } else {
    // 1여정 스캐쥴별 그룹핑 후 최저가 요금조건을 노출
    // 1여정 스캐쥴ID를 키로 갖는 맵을 생성하여 1여정 스캐쥴별 최저가 요금와 full 스캐쥴 ID를 저장
    schs.forEach(fschId => {
      const faresByOta = fares.get(fschId);
      if (faresByOta) {
        updateLowestFareBySchedule(fschId);
      }
    });
    // 1여정 목록에 노출될 full 스캐쥴ID들을 추출
    const filteredSchs = Array.from(schMapByLowestFare.values()).map(
      ([_, fschId]) => fschId,
    );
    finalList = sortSchs(
      filteredSchs,
      SelectTypeKeys.indivisual,
      flightResult,
    ).flat();
  }

  return (
    <>
      {sameFareTypes.length ? (
        <IndividualList
          fareType={fareType}
          flightResult={flightResult}
          itinerary={props.itinerary}
          promotions={promotions}
          scheduleIndex={nextIndex}
          scheduleTitle={scheduleTitles[nextIndex]}
          schs={sameFareTypes}
          selectedFareType={curFareType}
          showRecommendedHotels={!finalList.length}
          tripType={tripType}
          selectFlight={(
            sch: string,
            fareType: string,
            airline: string,
            labels: string[],
          ) => selectFlight(nextIndex, sch, fareType, airline, labels)}
          setMinFare={setMinFare}
        />
      ) : null}
      {finalList.length ? (
        <IndividualList
          fareType={fareType}
          flightResult={flightResult}
          isOtherFare={!!sameFareTypes.length}
          itinerary={props.itinerary}
          promotions={promotions}
          scheduleIndex={nextIndex}
          scheduleTitle={
            selectedFlightList.length ? '다른 요금' : scheduleTitles[nextIndex]
          }
          schs={finalList}
          showRecommendedHotels
          tripType={tripType}
          selectFlight={(
            sch: string,
            fareType: string,
            airline: string,
            labels: string[],
          ) => selectFlight(nextIndex, sch, fareType, airline, labels)}
          setMinFare={setMinFare}
        />
      ) : null}
    </>
  );
};

export default IndivisualListContainer;
